import { useEffect, useState } from "react";
import { Button } from "@app/components/atoms";
import { IconInvalidUrl, IconSpeaker, IconUser } from "@app/components/icons";
import { Avatar, Box, Skeleton } from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Machine } from "@app/types";
import { INVALID_URL, ReportType, INCIDENT_REPORTS } from "@app/constants";
import { scrollToTop } from "@app/helpers";
import { getUser } from "@app/api";
import { useParams } from "react-router-dom";
import { AllReportsDataDialog } from "@app/pages/Features/GroupList/GroupDetail/MachineReportsHistoryTab/AllReportsDataDialog";

type MachineOpenProps = {
  dbMachineOpen?: Machine[];
  isLoading?: boolean;
};

export function MachineOpen({
  dbMachineOpen,
  isLoading = false,
}: MachineOpenProps) {
  const { t } = useTranslation();
  const { groupId, machineId } = useParams();
  const [isShowFullData, setIsShowFullData] = useState(false);
  const [machineOpens, setMachineOpens] = useState<Machine[] | undefined>();
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogKey, setDialogKey] = useState<number>(0);
  const handleClick = (id: string) => {
    setSelectedReportId(id);
    setIsDialogOpen(true);
    setDialogKey((prev) => prev + 1);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedReportId("");
  };

  useEffect(() => {
    setMachineOpens(dbMachineOpen);
  }, [dbMachineOpen]);

  const [localeCode, setLocaleCode] = useState("");

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getUser();
        let timeFormat = response?.data?.data?.timeFormat || "yyyy/MM/dd";

        let isoLocaleCode = response?.data?.data?.isoLocaleCode;

        if (!timeFormat.includes("HH") && !timeFormat.includes("mm")) {
          timeFormat += " HH:mm";
        }

        setLocaleCode(isoLocaleCode);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  return (
    <>
      <div className="machine-open pt-16 bg-white w-100">
        <div className="flex align-items-center ml-16 mr-16">
          <IconSpeaker />
          {isLoading ? (
            <Skeleton
              variant="text"
              width={150}
              height={24}
              sx={{ ml: "5px" }}
            />
          ) : (
            <h4 className="fs-16 txt-gray m-0 ml-5">
              {t("machineOpenNumberTitle.report_interval", {
                postProcess: "interval",
                count: dbMachineOpen?.length,
              })}
            </h4>
          )}
        </div>
        <ul className="machine-open-list">
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <li key={i} className="machine-open-item">
                  <div className="flex align-items-start justify-content-between">
                    <div className="wrapper-content-left" style={{ flex: 1 }}>
                      <div
                        className="flex align-items-center"
                        style={{ gap: 8 }}
                      >
                        <Skeleton variant="circular" width={20} height={20} />
                        <Skeleton variant="text" width={100} height={16} />
                        <Skeleton
                          variant="rectangular"
                          width={60}
                          height={20}
                          sx={{ borderRadius: "4px" }}
                        />
                      </div>
                      <Skeleton
                        variant="text"
                        width="70%"
                        height={20}
                        sx={{ mt: 0.5 }}
                      />
                      <Skeleton
                        variant="text"
                        width="90%"
                        height={16}
                        sx={{ mt: 0.5 }}
                      />
                    </div>
                    <Skeleton
                      variant="text"
                      width={40}
                      height={16}
                      sx={{ ml: 1, flexShrink: 0 }}
                    />
                  </div>
                </li>
              ))
            : machineOpens
                ?.slice(0, isShowFullData ? machineOpens.length : 3)
                .map((db: any) => (
                  <li
                    key={db?.id}
                    className={`machine-open-item ${
                      !db?.isRead ? "has-icon-badge" : ""
                    }`}
                    onClick={() => handleClick(db?.id)}
                  >
                    <div className="flex align-items-start justify-content-between">
                      <div className="wrapper-content-left">
                        <div className="flex align-items-center">
                          <>
                            {db?.userPictureUrl ? (
                              db.userPictureUrl === INVALID_URL ? (
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  <IconInvalidUrl />
                                </Box>
                              ) : (
                                <Avatar
                                  sx={{
                                    width: 20,
                                    height: 20,
                                  }}
                                  alt="avatar"
                                  src={db.userPictureUrl}
                                  className="item-image"
                                  imgProps={{
                                    onError: () => {
                                      const newMachineOpens = machineOpens.map(
                                        (item) => {
                                          if (
                                            item.machineReportId ===
                                            db.machineReportId
                                          ) {
                                            item.userPictureUrl = INVALID_URL;
                                          }
                                          return item;
                                        },
                                      );
                                      setMachineOpens(newMachineOpens);
                                    },
                                  }}
                                />
                              )
                            ) : (
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <IconUser />
                              </Box>
                            )}
                          </>

                          <div className="item-name ml-5 fs-14 txt-gray-1 word-break txt-pre-wrap">
                            {localeCode === "ja"
                              ? `${db?.surname} ${db?.givenName}`
                              : `${db?.givenName} ${db?.surname}`}
                          </div>
                          <span
                            className={`item-label ml-12 txt-nowrap ${
                              db?.reportType === ReportType.INSPECTION
                                ? "label-inquire"
                                : "label-report"
                            }`}
                          >
                            {db?.reportType === ReportType.INSPECTION
                              ? t("labelInspection")
                              : t("groupDetailTab.reportTab")}
                          </span>
                        </div>
                        <p className="item-txt m-0 fs-16 txt-gray word-break txt-pre-wrap">
                          {db?.reportTitle}
                        </p>
                        <p className="item-des m-0 fs-14 txt-gray-1 word-break txt-pre-wrap">
                          {db?.firstReportComment}
                        </p>
                      </div>
                      <span className="item-txt-right txt-gray-1 txt-nowrap ml-12 fs-12">
                        {db?.timeSinceCommentCreation}
                      </span>
                    </div>
                  </li>
                ))}
        </ul>
        {dbMachineOpen && dbMachineOpen?.length > 3 && (
          <div className="text-center">
            <Button
              className="btn btn-no-border-new btn-see-more"
              onClick={() => {
                if (isShowFullData) {
                  scrollToTop();
                }
                setIsShowFullData(!isShowFullData);
              }}
            >
              <KeyboardArrowDown
                className={`icon-arrow ${isShowFullData ? "rotate-180" : ""}`}
              />
              {!isShowFullData ? t("seeMore") : t("dialogRefuel.close")}
            </Button>
          </div>
        )}
      </div>

      {isDialogOpen && selectedReportId && groupId && machineId && (
        <AllReportsDataDialog
          key={dialogKey}
          id={groupId}
          machineId={machineId}
          machineReportId={selectedReportId}
          reportType={INCIDENT_REPORTS}
          onClose={handleCloseDialog}
        />
      )}
    </>
  );
}
